/**
 * Wrapper for EdgeAgentDetector to match AgentDetector interface
 * This allows the middleware to work with EdgeAgentDetector in Edge Runtime
 *
 * This is a self-contained implementation to avoid import resolution issues
 * Includes proper Ed25519 signature verification for ChatGPT and other agents
 */

type DetectionInput = {
  userAgent?: string;
  ip?: string;
  ipAddress?: string;
  headers?: Record<string, string>;
  url?: string;
  method?: string;
  timestamp?: Date;
};

type DetectionResult = {
  isAgent: boolean;
  confidence: number;
  detectedAgent?: {
    type: string;
    name: string;
  };
  reasons: string[];
  verificationMethod?: string;
  forgeabilityRisk?: 'low' | 'medium' | 'high';
  timestamp: Date;
};

type EventHandler = (...args: any[]) => void;

// Import signature verifier
import { verifyAgentSignature, hasSignatureHeaders, isChatGPTSignature } from './signature-verifier';

// Known AI agent patterns
const AI_AGENT_PATTERNS = [
  { pattern: /chatgpt-user/i, type: 'chatgpt', name: 'ChatGPT' },
  { pattern: /claude-web/i, type: 'claude', name: 'Claude' },
  { pattern: /perplexitybot/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity-user/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity-ai/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity/i, type: 'perplexity', name: 'Perplexity' }, // Fallback
  { pattern: /bingbot/i, type: 'bing', name: 'Bing AI' },
  { pattern: /anthropic-ai/i, type: 'anthropic', name: 'Anthropic' },
];

// Known cloud provider IP ranges (simplified)
const CLOUD_PROVIDERS = {
  aws: ['54.', '52.', '35.', '18.', '3.'],
  gcp: ['35.', '34.', '104.', '107.', '108.'],
  azure: ['13.', '20.', '40.', '52.', '104.'],
};

/**
 * Self-contained EdgeAgentDetector implementation with proper signature verification
 */
class EdgeAgentDetector {
  async analyze(input: DetectionInput): Promise<DetectionResult> {
    const reasons: string[] = [];
    let detectedAgent: { type: string; name: string } | undefined;
    let verificationMethod: string | undefined;
    let confidence = 0;

    const headers = input.headers || {};
    const normalizedHeaders: Record<string, string> = {};

    // Normalize header names to lowercase
    for (const [key, value] of Object.entries(headers)) {
      normalizedHeaders[key.toLowerCase()] = value;
    }

    // Check for HTTP Message Signatures with proper cryptographic verification
    if (hasSignatureHeaders(headers)) {
      try {
        // Verify the signature cryptographically
        const signatureResult = await verifyAgentSignature(
          input.method || 'GET',
          input.url || '/',
          headers
        );

        if (signatureResult.isValid) {
          // Valid cryptographic signature - highest confidence
          confidence = signatureResult.confidence;
          reasons.push(`verified_signature:${signatureResult.agent?.toLowerCase() || 'unknown'}`);
          if (signatureResult.agent) {
            detectedAgent = { 
              type: signatureResult.agent.toLowerCase(), 
              name: signatureResult.agent 
            };
          }
          verificationMethod = signatureResult.verificationMethod;
          
          // Add keyid to reasons for debugging
          if (signatureResult.keyid) {
            reasons.push(`keyid:${signatureResult.keyid}`);
          }
        } else {
          // Invalid signature - suspicious
          confidence = Math.max(confidence, 0.3);
          reasons.push('invalid_signature');
          if (signatureResult.reason) {
            reasons.push(`signature_error:${signatureResult.reason}`);
          }
          
          // Still check if it claims to be ChatGPT
          if (isChatGPTSignature(headers)) {
            reasons.push('claims_chatgpt');
            detectedAgent = { type: 'chatgpt', name: 'ChatGPT (unverified)' };
          }
        }
      } catch (error) {
        // Error during verification
        console.error('[EdgeAgentDetector] Signature verification error:', error);
        confidence = Math.max(confidence, 0.2);
        reasons.push('signature_verification_error');
      }
    }

    // Check User-Agent patterns
    const userAgent = input.userAgent || input.headers?.['user-agent'] || '';
    if (userAgent) {
      for (const { pattern, type, name } of AI_AGENT_PATTERNS) {
        if (pattern.test(userAgent)) {
          // Give higher confidence to well-known AI agents that properly identify themselves
          const highConfidenceAgents = [
            'chatgpt',
            'claude',
            'perplexity',
            'anthropic',
          ];
          const patternConfidence = highConfidenceAgents.includes(type)
            ? 0.85
            : 0.5;

          confidence = Math.max(confidence, patternConfidence);
          reasons.push(`known_pattern:${type}`);
          if (!detectedAgent) {
            detectedAgent = { type, name };
            verificationMethod = 'pattern';
          }
          break;
        }
      }
    }

    // Check AI-specific headers
    const aiHeaders = [
      'openai-conversation-id',
      'openai-ephemeral-user-id',
      'anthropic-client-id',
      'x-goog-api-client',
      'x-ms-copilot-id',
    ];

    const foundAiHeaders = aiHeaders.filter(
      header => normalizedHeaders[header]
    );
    if (foundAiHeaders.length > 0) {
      confidence = Math.max(confidence, 0.6);
      reasons.push(`ai_headers:${foundAiHeaders.length}`);
    }

    // Check cloud provider IPs
    const ip = input.ip || input.ipAddress;
    if (
      ip &&
      !normalizedHeaders['x-forwarded-for'] &&
      !normalizedHeaders['x-real-ip']
    ) {
      for (const [provider, prefixes] of Object.entries(CLOUD_PROVIDERS)) {
        if (prefixes.some(prefix => ip.startsWith(prefix))) {
          confidence = Math.max(confidence, 0.4);
          reasons.push(`cloud_provider:${provider}`);
          break;
        }
      }
    }

    // Boost confidence for combinations (but not if already at 1.0 from signature)
    if (reasons.length > 2 && confidence < 1.0) {
      confidence = Math.min(confidence * 1.2, 0.95);
    }

    return {
      isAgent: confidence > 0.3,
      confidence,
      ...(detectedAgent && { detectedAgent }),
      reasons,
      ...(verificationMethod && { verificationMethod }),
      forgeabilityRisk: verificationMethod === 'signature' ? 'low' : (confidence > 0.8 ? 'medium' : 'high'),
      timestamp: new Date(),
    };
  }
}

/**
 * Wrapper that provides event emitter functionality
 */
export class EdgeAgentDetectorWrapper {
  private detector: EdgeAgentDetector;
  private events: Map<string, EventHandler[]> = new Map();

  constructor(_config?: any) {
    // Config is accepted but not used by EdgeAgentDetector
    this.detector = new EdgeAgentDetector();
  }

  async analyze(input: DetectionInput): Promise<DetectionResult> {
    const result = await this.detector.analyze(input);

    // Emit events if there are listeners
    if (result.isAgent && this.events.has('agent.detected')) {
      const handlers = this.events.get('agent.detected') || [];
      handlers.forEach(handler => handler(result, input));
    }

    return result;
  }

  on(event: string, handler: EventHandler): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(handler);
  }

  emit(event: string, ...args: any[]): void {
    const handlers = this.events.get(event) || [];
    handlers.forEach(handler => handler(...args));
  }

  async init(): Promise<void> {
    // EdgeAgentDetector doesn't need initialization
    return;
  }
}
